import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { rawReadingError, turnDefect } from '@/lib/conversation-model';
import { fetchTurnPrompts } from '@/lib/session-api';
import { turnSeatLabel } from '@/lib/session-labels';
import type { SessionTurn, TurnPrompts } from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { LayerLine, TurnLayerShell } from './layer-shell';

/**
 * The Brut layer of one turn (§8.7, §12.3): what the scribe answered, what the
 * seats were served, and the identifiers.
 *
 * ## The served inputs are read on a CLICK and never with the session
 *
 * « C'est la plus grosse chose qu'un tour produit et la moins souvent lue »
 * (§8.7), which is why it lives behind its own route. So the panel is MOUNTED
 * when the button is pressed - `enabled: open` expressed as a mount, the régime
 * `StepEditor` already applies to a criterion's card - and a conversation of
 * forty turns opened with the Brut layer on all of them still makes one request
 * per seat somebody actually asked for.
 *
 * ## The raw output is printed, never re-serialised
 *
 * `scribeRaw` is « la seule chose qu'un tour produit que personne ne peut
 * reconstituer » (§8.7). Pretty-printing it would repair, silently, exactly the
 * defect a reader came here to see - a truncated JSON, a stray comma, a fence
 * the model wrapped it in - so it is shown as it arrived.
 *
 * The line above it is « Erreur de lecture », and it NAMES the defect since
 * issue #96 - label and cause inside the sentence, so what the turn recorded
 * and what the text does when it is read again today stay two readable facts.
 * On a truncated turn it says the ceiling before re-reading anything: the
 * parser's « Unexpected end of JSON input » is true and sends a reader hunting
 * for a shape bug where the provider simply stopped writing.
 */

export interface LayerBrutProps {
  sessionId: string;
  turn: SessionTurn;
  open: boolean;
  onToggle: () => void;
}

export function LayerBrut({ sessionId, turn, open, onToggle }: LayerBrutProps) {
  const [prompts, setPrompts] = useState(false);
  // Null on a readable turn; « Malformé » or « Tronqué » otherwise (issue #96).
  const defect = turnDefect(turn);

  return (
    <TurnLayerShell
      layer="brut"
      /*
        This layer says what it HOLDS, defect or not: a fold that only named the
        defect would stop telling a reader that the served inputs and the
        identifiers are behind it - which is the whole reason to open it on a
        turn nobody can read.
      */
      hint={
        defect === null
          ? 'JSON du scribe, entrées servies, identifiants'
          : `${defect.cause}, entrées servies, identifiants`
      }
      open={open}
      onToggle={onToggle}
    >
      {defect === null ? null : (
        <dl className="flex flex-col gap-2">
          {/*
            « Erreur de lecture » and never the defect's own word: the line under
            it is what the raw text does when it is READ AGAIN today, which is a
            different question from what the turn recorded. The defect is named
            inside the sentence, so the two are on one line and neither passes
            for the other.
          */}
          <LayerLine label="Erreur de lecture">
            <span className="text-jaune-deep">
              {defect.label} ({defect.cause}) · {rawReadingError(turn.scribeRaw, defect)}
            </span>
          </LayerLine>
        </dl>
      )}

      <p className="text-gray-600 text-caption">
        Sortie du scribe telle que reçue (<code className="font-mono">scribeRaw</code>)
      </p>
      {turn.scribeRaw === '' ? (
        <p className="text-gray-600">Ce tour n’a pas conservé la sortie brute du scribe.</p>
      ) : (
        <pre
          data-testid={SESSIONS_TEST_IDS.turnRaw}
          className="max-h-65 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-gray-900 text-caption leading-5"
        >
          {turn.scribeRaw}
        </pre>
      )}

      {prompts ? (
        <ServedPrompts
          queryKey={['session-turn-prompts', sessionId, turn.id]}
          load={() => fetchTurnPrompts(sessionId, turn.id)}
          empty="Ce tour n’a conservé aucune entrée servie : il a été joué avant que la table ne les garde."
        />
      ) : (
        <p className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid={SESSIONS_TEST_IDS.turnPrompts}
            onClick={() => setPrompts(true)}
            className="cursor-pointer font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
          >
            Voir les entrées servies aux deux sièges
          </button>
          <span className="text-gray-500 text-caption">
            chargées à la demande, table <code className="font-mono">session_turn_prompts</code>
          </span>
        </p>
      )}

      <p className="break-all font-mono text-gray-500 text-caption">
        tour {turn.id} · index {turn.index} · étape {turn.stepId} · session {turn.sessionId} ·
        malformed: {String(turn.malformed)} · truncated: {String(turn.truncated)}
      </p>
    </TurnLayerShell>
  );
}

/**
 * The prompts served for one turn - or for one bubble of a page played in the
 * conversation (issue #186) - read when this component MOUNTS.
 *
 * A component of its own for that reason alone: a `useQuery` in the layer above
 * would fire for every turn the Brut layer is open on, which is every turn as
 * soon as the bar switch is pressed. What it reads is the caller's: the key and
 * the fetch are props, the sentence of the empty answer too, so the bubble's
 * Brut layer draws the same entries under its own route.
 */
export function ServedPrompts({
  queryKey,
  load,
  empty,
}: {
  queryKey: readonly unknown[];
  load: () => Promise<TurnPrompts>;
  empty: string;
}) {
  const served = useQuery({
    queryKey,
    queryFn: load,
    // The row is immutable once written (§8.7): re-reading it would be a request
    // that can never answer anything new.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  if (served.isPending) {
    return <p className="text-gray-600 text-small">Lecture des entrées servies…</p>;
  }
  if (served.isError || served.data === undefined) {
    return (
      <p role="alert" className="text-rouge text-small">
        Les entrées servies n’ont pas pu être lues. Elles sont purgées avec la session (§12.4).
      </p>
    );
  }
  if (served.data.prompts.length === 0) {
    return <p className="text-gray-600 text-small">{empty}</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {served.data.prompts.map((prompt, index) => (
        <section
          // By position and not by seat: a scribe sent back once (#127) serves
          // two entries under the same seat.
          key={`${prompt.seat}-${index}`}
          data-testid={SESSIONS_TEST_IDS.turnPrompt}
          data-seat={prompt.seat}
          className="flex flex-col gap-1.5"
        >
          <h5 className="font-semibold text-gray-600 text-caption uppercase tracking-wide">
            Entrée servie · {turnSeatLabel(prompt.seat)}
          </h5>
          <pre className="max-h-65 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-gray-900 text-caption leading-5">
            {prompt.system}
          </pre>
          {prompt.messages.map((message, index) => (
            <pre
              key={`${message.role}-${index}`}
              className="max-h-65 overflow-auto rounded-md border border-gray-200 bg-white p-3 font-mono text-gray-900 text-caption leading-5"
            >
              {message.role === 'user' ? '[user] ' : '[assistant] '}
              {message.content}
            </pre>
          ))}
        </section>
      ))}
    </div>
  );
}
